import {
  PhoneMultiFactorInfo,
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  getMultiFactorResolver,
  MultiFactorResolver,
  signOut
} from "firebase/auth";
import { useEffect, useId, useMemo, useState } from "react";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { AuthEmailAPI } from "@/api/authEmailApi";
import { APIError } from "@/api/utils/errors";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useMultiFactorAuth } from "../hooks/useMultiFactorAuth";
import { CustomPhoneInput } from "./common/PhoneInput";
import { ReauthDialog } from "./ReauthDialog";

export const MFAManagement = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [reauthState, setReauthState] = useState<{
    show: boolean;
    action: "enroll" | "unenroll";
    pendingFactorId?: string;
    pendingPhoneNumber?: string;
    showMFA?: boolean;
    mfaResolver?: MultiFactorResolver;
    verificationId?: string;
  } | null>(null);
  const auth = getAuth();

  const {
    isEnrolling,
    error,
    enrolledFactors,
    startEnrollment,
    completeEnrollment,
    unenrollFactor,
    getRecaptchaVerifier,
    verifyLoginCode,
    initiateLoginVerification
  } = useMultiFactorAuth();

  const showToast = useMemo(() => {
    return (params: {
      status: "success" | "error" | "warning" | "info";
      title: string;
      description?: string;
    }) => {
      const { status, title, description } = params;
      const options = {
        description,
        duration: 4000,
        position: "bottom-left" as const
      };
      if (status === "success") toast.success(title, options);
      else if (status === "error") toast.error(title, options);
      else if (status === "warning") toast.warning(title, options);
      else toast.info(title, options);
    };
  }, []);
  const verificationCodeId = useId();

  // Check email verification status
  const checkEmailVerification = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Ensure we have a fresh token before reloading user state.
      await user.getIdToken(true);
      await user.reload();
      setIsEmailVerified(!!auth.currentUser?.emailVerified);
    } catch (error: any) {
      const code = error?.code;
      if (
        code === "auth/user-token-expired" ||
        code === "auth/id-token-expired" ||
        code === "auth/invalid-user-token"
      ) {
        await signOut(auth);
        showToast({
          title: "Session Expired",
          description: "Please log in again to manage MFA settings.",
          status: "warning"
        });
      }
    }
  };

  // Send verification email
  const sendVerificationEmail = async () => {
    if (auth.currentUser) {
      setIsSendingVerification(true);
      try {
        const status = await AuthEmailAPI.sendVerificationEmail();
        if (status === "already_verified") {
          await checkEmailVerification();
          return;
        }
        showToast({
          title: "Verification Email Sent",
          description: "Please check your email to verify your address",
          status: "success"
        });
      } catch (error: any) {
        if (error instanceof APIError && error.status === 401) {
          await signOut(auth);
          showToast({
            title: "Session Expired",
            description:
              "Please log in again, then resend the verification email.",
            status: "warning"
          });
          return;
        }
        showToast({
          title: "Error",
          description: error.message,
          status: "error"
        });
      } finally {
        setIsSendingVerification(false);
      }
    }
  };

  // Check verification status when component mounts and after email verification
  useEffect(() => {
    checkEmailVerification();
    const interval = setInterval(checkEmailVerification, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const handleReauthenticate = async (password: string) => {
    if (!auth.currentUser?.email) return;

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        password
      );
      await reauthenticateWithCredential(auth.currentUser, credential);

      if (reauthState?.action === "enroll" && reauthState.pendingPhoneNumber) {
        await startEnrollment(reauthState.pendingPhoneNumber);
        setShowVerification(true);
      } else if (
        reauthState?.action === "unenroll" &&
        reauthState.pendingFactorId
      ) {
        await unenrollFactor(reauthState.pendingFactorId);
      }
      setReauthState(null);
    } catch (error: any) {
      if (error.code === "auth/multi-factor-auth-required") {
        const resolver = getMultiFactorResolver(auth, error);
        await initiateLoginVerification(resolver);
        setReauthState({
          ...reauthState!,
          showMFA: true,
          mfaResolver: resolver
        });
      } else {
        showToast({
          title: "Error",
          description: error.message,
          status: "error"
        });
      }
    }
  };

  const handleStartEnrollment = async () => {
    try {
      await startEnrollment(phoneNumber);
      setShowVerification(true);
    } catch (error: any) {
      if (error.code === "auth/requires-recent-login") {
        setReauthState({
          show: true,
          action: "enroll",
          pendingPhoneNumber: phoneNumber
        });
      } else {
        showToast({
          title: "Error",
          description: error.message,
          status: "error"
        });
      }
    }
  };

  const handleCompleteEnrollment = async () => {
    await completeEnrollment(verificationCode);
    if (!error) {
      setShowVerification(false);
      setPhoneNumber("");
      setVerificationCode("");
      showToast({
        title: "MFA Enrolled",
        description:
          "Two-factor authentication has been enabled for your account",
        status: "success"
      });
    }
  };

  const handleUnenroll = async (factorId: string) => {
    try {
      await unenrollFactor(factorId);
      if (!error) {
        showToast({
          title: "MFA Removed",
          description: "Two-factor authentication has been disabled",
          status: "info"
        });
      }
    } catch (error: any) {
      if (error.code === "auth/requires-recent-login") {
        setReauthState({
          show: true,
          action: "unenroll",
          pendingFactorId: factorId
        });
      } else {
        showToast({
          title: "Error",
          description: error.message,
          status: "error"
        });
      }
    }
  };

  const handlePhoneChange = (phone: string) => {
    // Ensure phone number starts with +
    const formattedPhone = phone.startsWith("+") ? phone : `+${phone}`;
    setPhoneNumber(formattedPhone);
  };

  const canSendCode = phoneNumber.trim().length > 0 && !reauthState?.show;

  return (
    <div className="w-full">
      {!isEmailVerified ? (
        <Alert className="w-full border-warn-line bg-warn-soft">
          <TriangleAlert className="text-warn" />
          <AlertTitle>Verify Your Email</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-2">
            You need to verify your email before enabling two-factor
            authentication.
            <Button
              disabled={isSendingVerification}
              onClick={sendVerificationEmail}
            >
              {isSendingVerification ? <Spinner /> : null}
              Send Verification Email
            </Button>
          </AlertDescription>
        </Alert>
      ) : enrolledFactors && enrolledFactors.length > 0 ? (
        <div className="flex w-full flex-col items-start">
          <div className="mb-3 flex w-full flex-col items-start gap-1">
            <span className="text-base font-medium text-fg">
              Two-Factor Authentication
            </span>
            <span className="text-sm text-fg-3">
              Your account is protected with a verification code sent via text
              message.
            </span>
          </div>
          <div className="flex w-full flex-col divide-y divide-line-1 border-y border-line-1">
            {enrolledFactors.map((factor) => (
              <div
                key={factor.uid}
                className="flex w-full items-center justify-between gap-4 py-3"
              >
                <div className="flex min-w-0 flex-col items-start gap-1">
                  <span className="text-base font-medium text-fg">
                    Text Message
                  </span>
                  <span className="text-sm text-fg-3">
                    {String((factor as PhoneMultiFactorInfo).phoneNumber || "")}
                  </span>
                </div>
                <Button
                  variant="danger"
                  onClick={() => handleUnenroll(factor.uid)}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : !showVerification ? (
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-sm text-fg-3">
            We'll send you a code to confirm it's really you.
          </span>

          <CustomPhoneInput
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="Phone Number"
            inputWidth="100%"
            size="large"
          />
          <div className="flex w-full justify-end">
            <Button
              variant="primary"
              disabled={!canSendCode || isEnrolling}
              onClick={handleStartEnrollment}
            >
              {isEnrolling ? <Spinner /> : null}
              Send Code
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-sm text-fg-3">
            Enter the verification code we sent to your phone number.
          </span>

          <div className="flex w-full flex-col gap-1.5">
            <Label htmlFor={verificationCodeId}>Verification Code</Label>
            <Input
              id={verificationCodeId}
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter code"
            />
          </div>
          <div className="flex w-full justify-end">
            <Button
              variant="primary"
              disabled={isEnrolling}
              onClick={handleCompleteEnrollment}
            >
              {isEnrolling ? <Spinner /> : null}
              Verify
            </Button>
          </div>
        </div>
      )}

      {reauthState?.show && (
        <ReauthDialog
          onReauthenticate={handleReauthenticate}
          actionText={
            reauthState.action === "enroll" ? "enable MFA" : "remove MFA"
          }
          showMFA={reauthState.showMFA}
          phoneNumber={
            (reauthState.mfaResolver?.hints[0] as PhoneMultiFactorInfo)
              ?.phoneNumber
          }
          onMFACode={async (code) => {
            if (reauthState.mfaResolver) {
              await verifyLoginCode(reauthState.mfaResolver, code);
              // Retry the unenroll operation after successful MFA verification
              if (
                reauthState.action === "unenroll" &&
                reauthState.pendingFactorId
              ) {
                try {
                  await unenrollFactor(reauthState.pendingFactorId);
                  if (!error) {
                    showToast({
                      title: "MFA Removed",
                      description:
                        "Two-factor authentication has been disabled",
                      status: "info"
                    });
                  }
                } catch (error: any) {
                  showToast({
                    title: "Error",
                    description: error.message,
                    status: "error"
                  });
                }
              }
              setReauthState(null);
            }
          }}
        />
      )}
      {/* Needed for Firebase phone auth reCAPTCHA verifier */}
      <div id="recaptcha-container" style={{ display: "none" }} />
    </div>
  );
};
